import React from 'react';

import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

type ConfirmDialogProps = {
  /** ダイアログの表示/非表示 */
  open: boolean;
  /** ダイアログを閉じるハンドラ */
  onClose: () => void;
  /** 実行ハンドラ */
  onConfirm: () => void;
  /** ダイアログタイトル */
  title: string;
  /** ダイアログ内に表示するメッセージまたはカスタムコンテンツ */
  children?: React.ReactNode;
  /** 実行ボタンのラベル（省略時: "実行"） */
  confirmLabel?: string;
  /** 実行ボタンのカラー（省略時: "primary"） */
  confirmColor?: 'primary' | 'error' | 'success' | 'warning' | 'info';
  /** 外部から実行ボタンの非活性を追加制御する */
  confirmDisabled?: boolean;
};

/**
 * 汎用確認ダイアログ
 *
 * タイトル、メッセージ（children）、実行ボタンの文言・色を自由に設定できる。
 */
const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  children,
  confirmLabel = '実行',
  confirmColor = 'primary',
  confirmDisabled = false,
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        elevation: 0,
        sx: { borderRadius: 3, border: 1, borderColor: 'divider' },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
        }}
      >
        <Typography variant="subtitle1" fontWeight={600}>
          {title}
        </Typography>
        <IconButton size="small" onClick={onClose} sx={{ color: 'text.secondary' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      {children && (
        <DialogContent>
          <Box sx={{ pt: 1 }}>{children}</Box>
        </DialogContent>
      )}

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}>
          キャンセル
        </Button>
        <Button
          variant="contained"
          color={confirmColor}
          onClick={onConfirm}
          disabled={confirmDisabled}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: 2,
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' },
          }}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmDialog;
